import { useEffect, useState } from 'react';

import { PASS_CATEGORIES } from '@/lib/passes-format';
import { Button } from '@/components/ui/button';
import { Chip } from '@/components/ui/chip';
import { Icon } from '@/components/ui/icon';
import { Spinner } from '@/components/ui/spinner';

const MAPBOX_TOKEN = (import.meta.env['VITE_MAPBOX_PUBLIC_TOKEN'] ?? '') as string;
const DELHI_CENTER = [77.209, 28.6139] as const;

export interface BrowseParams {
  category: string;
  gps: string;
  /** RFC3339 visit timestamp. */
  date: string;
}

/** GPS as "<lat>, <lng>" (6dp, comma+space) — same wire format the rides flow uses. */
function toGps(lat: number, lng: number): string {
  return `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
}

/** datetime-local default ~2d out, matching the Workbench certification window. */
function defaultVisitLocal(): string {
  const d = new Date(Date.now() + 2 * 24 * 60 * 60 * 1000);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function minimumVisitLocal(): string {
  const d = new Date();
  d.setSeconds(0, 0);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/**
 * Category-first discovery: pick an experience type, a coarse "near me" location and a
 * visit time, then Browse. No map (entry-pass discovery is category-led; a map would
 * pull ~550KB of mapbox-gl onto this route for a single search-center).
 */
export function CategoryPicker({
  onBrowse,
  pending,
}: {
  onBrowse: (params: BrowseParams) => void;
  pending: boolean;
}) {
  const [category, setCategory] = useState(PASS_CATEGORIES[0]?.code ?? 'MUSEUM');
  const [gps, setGps] = useState('');
  const [manualGps, setManualGps] = useState('');
  const [placeQuery, setPlaceQuery] = useState('');
  const [placeResults, setPlaceResults] = useState<{ name: string; gps: string }[]>([]);
  const [placesOpen, setPlacesOpen] = useState(false);
  const [locating, setLocating] = useState(false);
  const [locError, setLocError] = useState('');
  const [visitLocal, setVisitLocal] = useState(defaultVisitLocal);

  useEffect(() => {
    const query = placeQuery.trim();
    if (!MAPBOX_TOKEN || query.length < 3) {
      setPlaceResults([]);
      return;
    }
    const controller = new AbortController();
    const timer = setTimeout(async () => {
      try {
        const response = await fetch(
          `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(query)}.json` +
            `?access_token=${MAPBOX_TOKEN}&country=in&limit=5&proximity=${DELHI_CENTER[0]},${DELHI_CENTER[1]}`,
          { signal: controller.signal }
        );
        if (!response.ok) return;
        const json = (await response.json()) as {
          features?: { place_name: string; center: [number, number] }[];
        };
        setPlaceResults(
          (json.features ?? []).map((feature) => ({
            name: feature.place_name,
            gps: `${feature.center[1].toFixed(6)}, ${feature.center[0].toFixed(6)}`,
          }))
        );
        setPlacesOpen(true);
      } catch {
        // Ignore aborted requests and geocoder outages; manual GPS remains available.
      }
    }, 300);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [placeQuery]);

  function useMyLocation() {
    if (!navigator.geolocation) {
      setLocError('Location is not available in this browser.');
      return;
    }
    setLocating(true);
    setLocError('');
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const nextGps = toGps(pos.coords.latitude, pos.coords.longitude);
        setGps(nextGps);
        setManualGps(nextGps);
        setLocating(false);
      },
      () => {
        setLocError("Couldn't get your location — browsing city-wide.");
        setLocating(false);
      },
      { enableHighAccuracy: true, timeout: 10_000 }
    );
  }

  function browse() {
    // A datetime-local input can be cleared/partial → new Date('') is Invalid Date and
    // .toISOString() throws. Fall back to a valid default so we never crash the click and
    // always send a well-formed RFC3339 (the backend rejects malformed visit dates).
    const parsed = new Date(visitLocal);
    const fallback = new Date(Date.now() + 2 * 24 * 60 * 60 * 1000);
    const date = Number.isNaN(parsed.getTime()) || parsed.getTime() < Date.now()
      ? fallback.toISOString()
      : parsed.toISOString();
    onBrowse({ category, gps: manualGps.trim() || gps, date });
  }

  return (
    <div className="wb-card wb-card--pad wb-col wb-gap-16">
      <div className="wb-col wb-gap-8">
        <p className="wb-label" style={{ margin: 0 }}>
          What are you in the mood for?
        </p>
        <div className="wb-row wb-wrap wb-gap-8">
          {PASS_CATEGORIES.map((c) => (
            <Chip key={c.code} active={category === c.code} onClick={() => setCategory(c.code)}>
              {c.label}
            </Chip>
          ))}
        </div>
      </div>

      <div className="wb-col wb-gap-12">
        <div className="wb-col wb-gap-4">
          <span className="wb-label" style={{ margin: 0 }}>
            Location
          </span>
          <div style={{ position: 'relative' }}>
            <input
              aria-label="Search place"
              value={placeQuery}
              onChange={(e) => setPlaceQuery(e.target.value)}
              onFocus={() => placeResults.length > 0 && setPlacesOpen(true)}
              placeholder="Search a place…"
              className="wb-field"
            />
            {placesOpen && placeResults.length > 0 && (
              <ul
                style={{
                  position: 'absolute',
                  zIndex: 10,
                  width: '100%',
                  margin: '4px 0 0',
                  padding: 0,
                  listStyle: 'none',
                  background: 'var(--surface)',
                  border: '1px solid var(--line)',
                  borderRadius: 'var(--r-md)',
                  overflow: 'hidden',
                }}
              >
                {placeResults.map((result) => (
                  <li key={`${result.name}:${result.gps}`}>
                    <button
                      type="button"
                      onClick={() => {
                        setPlaceQuery(result.name);
                        setManualGps(result.gps);
                        setGps(result.gps);
                        setPlacesOpen(false);
                      }}
                      style={{
                        display: 'block',
                        width: '100%',
                        padding: '10px 12px',
                        border: 0,
                        background: 'transparent',
                        color: 'var(--ink)',
                        textAlign: 'left',
                        cursor: 'pointer',
                      }}
                    >
                      {result.name}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <button
            type="button"
            onClick={useMyLocation}
            disabled={locating}
            className={`wb-field-btn ${gps ? '' : 'is-empty'}`}
          >
            {locating ? (
              <Spinner />
            ) : (
              <Icon name="pin" size={20} style={{ color: 'var(--cat-pass)' }} />
            )}
            <span>{gps ? 'Use my location instead' : 'Use my location'}</span>
          </button>
        </div>

        <div className="wb-col wb-gap-4">
          <label className="wb-label" htmlFor="visit-when" style={{ margin: 0 }}>
            Visit date &amp; time
          </label>
          <input
            id="visit-when"
            type="datetime-local"
            min={minimumVisitLocal()}
            value={visitLocal}
            onChange={(e) => setVisitLocal(e.target.value)}
            className="wb-field"
          />
        </div>
      </div>

      {locError && <p className="wb-tiny">{locError}</p>}

      <Button variant="primary" block onClick={browse} disabled={pending}>
        {pending ? 'Searching…' : 'Browse experiences'}
      </Button>
    </div>
  );
}
